import React, { useEffect, useMemo, useRef, useState } from "react";
import { isGoogleMapsConfigured, loadGoogleMaps } from "../../utils/googleMaps";
import {
  COVERAGE_STATUSES,
  STATUS_COLOR,
  clusterPoints,
  isCoLocated,
  strongestStatus,
} from "../../utils/coverageMap";
import type { MapScreen } from "../../schemas/campaignSchema";

type LocatedScreen = MapScreen & { latitude: number; longitude: number };

interface CoverageMapCanvasProps {
  /** Screens with a position, already filtered by the status buttons. */
  screens: LocatedScreen[];
  selectedIds: string[];
  /** The screens to bring into view. Empty means "all of them". */
  frameIds: string[];
  onSelect: (screenIds: string[]) => void;
  /** A cluster was clicked and the map zoomed into it: these are its screens. */
  onZoomInto: (screenIds: string[]) => void;
}

// Roughly the centre of India, shown until there is something to frame.
const DEFAULT_CENTER = { lat: 22.5, lng: 79 };
const DEFAULT_ZOOM = 4;
const SINGLE_PIN_ZOOM = 15;
const MAX_ZOOM = 19;

const RED = "#dc2626";
const DARK = "#1f2937";

const svgUrl = (svg: string) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

// The pin is drawn on a 40x50 canvas: a teardrop whose tip sits at (20, 46),
// with room top-right for the on-air badge and a soft shadow under the tip.
const PIN_W = 40;
const PIN_H = 50;
const PIN_TIP = { x: 20, y: 46 };

/**
 * One screen: a map pin in its status colour with a small screen glyph in the
 * head. A red badge on the pin means the screen is showing the brand's ad right
 * now — the map's "on air", separate from the Live colour, which only says the
 * campaign is inside its dates. A selected pin gets a dark outline.
 */
const pinSvg = (color: string, onAir: boolean, selected: boolean) => {
  const outline = selected ? "#111827" : "#fff";
  const badge = onAir
    ? `<circle cx="32" cy="8" r="6" fill="${RED}" stroke="#fff" stroke-width="2"/>`
    : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_W}" height="${PIN_H}" viewBox="0 0 ${PIN_W} ${PIN_H}">
<ellipse cx="${PIN_TIP.x}" cy="${PIN_TIP.y}" rx="6" ry="2" fill="#000" opacity="0.25"/>
<path d="M20 3C12.3 3 6 9.2 6 16.9c0 9.9 12.3 24.2 12.9 24.9a1.5 1.5 0 0 0 2.2 0C21.7 41.1 34 26.8 34 16.9 34 9.2 27.7 3 20 3z" fill="${color}" stroke="${outline}" stroke-width="${selected ? 2.5 : 2}"/>
<circle cx="20" cy="17" r="8" fill="#fff"/>
<rect x="15" y="13.5" width="10" height="6.5" rx="1" fill="${color}"/>
<rect x="18" y="20.5" width="4" height="1.6" rx="0.5" fill="${color}"/>
${badge}</svg>`;
};

const pinIcon = (color: string, onAir: boolean, selected: boolean, maps: any) => {
  // A selected pin is drawn a size up so it stands out from its neighbours.
  const scale = selected ? 1.2 : 1;
  const w = PIN_W * scale;
  const h = PIN_H * scale;
  return {
    url: svgUrl(pinSvg(color, onAir, selected)),
    scaledSize: new maps.Size(w, h),
    anchor: new maps.Point(PIN_TIP.x * scale, PIN_TIP.y * scale),
  };
};

/** Several screens close together: a dark disc with the count. */
const clusterSvg = (count: number, onAir: boolean, selected: boolean) => {
  const size = count >= 100 ? 52 : count >= 10 ? 46 : 40;
  const c = size / 2;
  const ring = onAir
    ? `<circle cx="${c}" cy="${c}" r="${c - 2}" fill="none" stroke="${RED}" stroke-width="3"/>`
    : "";
  const halo = selected
    ? `<circle cx="${c}" cy="${c}" r="${c - 0.5}" fill="none" stroke="#111827" stroke-width="2"/>`
    : "";
  return {
    size,
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${halo}${ring}<circle cx="${c}" cy="${c}" r="${c - 7}" fill="${DARK}" stroke="#fff" stroke-width="2"/><text x="${c}" y="${c + 4}" text-anchor="middle" font-family="Arial,sans-serif" font-size="12" font-weight="700" fill="#fff">${count}</text></svg>`,
  };
};

const clusterIcon = (count: number, onAir: boolean, selected: boolean, maps: any) => {
  const { size, svg } = clusterSvg(count, onAir, selected);
  return {
    url: svgUrl(svg),
    scaledSize: new maps.Size(size, size),
    anchor: new maps.Point(size / 2, size / 2),
  };
};

/**
 * What the pins mean, drawn with the same SVGs the map uses. A one-line strip
 * under the map rather than a box on it, so it never hides a pin.
 */
const MapLegend: React.FC = () => (
  <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 pt-2 text-xs text-gray-600">
    {COVERAGE_STATUSES.map(({ status, label, hint }) => (
      <li key={status} className="flex items-center gap-1.5" title={hint}>
        <img
          src={svgUrl(pinSvg(STATUS_COLOR[status], false, false))}
          alt=""
          className="h-5 w-4"
        />
        {label}
      </li>
    ))}
    <li className="flex items-center gap-1.5">
      <img
        src={svgUrl(pinSvg(STATUS_COLOR.ACTIVE, true, false))}
        alt=""
        className="h-5 w-4"
      />
      Red badge = on air now
    </li>
    <li className="flex items-center gap-1.5">
      <img
        src={svgUrl(clusterSvg(3, false, false).svg)}
        alt=""
        className="h-4 w-4"
      />
      Number = screens close together, click to zoom
    </li>
  </ul>
);

/**
 * The Google Maps half of the coverage map. Everything it draws is decided by
 * the props — filtering, counts and the city rollup live in utils/coverageMap —
 * so this component only turns "these screens" into pins.
 */
const CoverageMapCanvas: React.FC<CoverageMapCanvasProps> = ({
  screens,
  selectedIds,
  frameIds,
  onSelect,
  onZoomInto,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const lastFrameKey = useRef<string | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onZoomIntoRef = useRef(onZoomInto);
  onZoomIntoRef.current = onZoomInto;

  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  // Clustering is a function of zoom alone (the grid is in world pixels), so a
  // pan never needs a redraw and only a zoom change does.
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  // Create the map once.
  useEffect(() => {
    if (!isGoogleMapsConfigured) return;
    let cancelled = false;
    let listener: any;

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        const map = new maps.Map(containerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          maxZoom: MAX_ZOOM,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          // The distance bar, bottom right, so "how far apart" is readable.
          scaleControl: true,
        });
        listener = map.addListener("zoom_changed", () =>
          setZoom(Math.round(map.getZoom() ?? DEFAULT_ZOOM)),
        );
        mapRef.current = map;
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      listener?.remove?.();
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      mapRef.current = null;
    };
  }, []);

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Redraw the pins whenever the screens, selection or zoom change.
  useEffect(() => {
    const map = mapRef.current;
    const maps = (window as any).google?.maps;
    if (!ready || !map || !maps) return;

    markersRef.current.forEach((m) => {
      maps.event.clearInstanceListeners(m);
      m.setMap(null);
    });

    markersRef.current = clusterPoints(screens, zoom).map((cluster) => {
      const members = cluster.items;
      const onAir = members.some((s) => s.campaigns.some((c) => c.isPlayingNow));
      const isSelected = members.some((s) => selected.has(s.screenId));

      const marker = new maps.Marker({
        map,
        position: { lat: cluster.lat, lng: cluster.lng },
        icon:
          members.length === 1
            ? pinIcon(
                STATUS_COLOR[strongestStatus(members[0].campaigns)],
                onAir,
                isSelected,
                maps,
              )
            : clusterIcon(members.length, onAir, isSelected, maps),
        title:
          members.length === 1
            ? members[0].screenName
            : `${members.length} screens`,
        zIndex: isSelected ? 2000 : onAir ? 1000 : members.length,
      });

      marker.addListener("click", () => {
        if (members.length === 1 || isCoLocated(members)) {
          // One screen, or several at one address: nothing to zoom into.
          onSelectRef.current(members.map((s) => s.screenId));
          return;
        }
        const bounds = new maps.LatLngBounds();
        members.forEach((s) =>
          bounds.extend({ lat: s.latitude, lng: s.longitude }),
        );
        map.fitBounds(bounds, 80);
        // Zoom first, then tell the panel: if it moves to a city or an area,
        // the framing effect below re-frames to that, which wins.
        onZoomIntoRef.current(members.map((s) => s.screenId));
      });

      return marker;
    });
  }, [ready, screens, selected, zoom]);

  // Bring the right screens into view — but only when what should be framed
  // changes, so a refresh or a click on a pin does not yank the map around.
  const frameKey = useMemo(() => {
    const ids = frameIds.length ? frameIds : screens.map((s) => s.screenId);
    return [...ids].sort().join(",");
  }, [frameIds, screens]);

  useEffect(() => {
    const map = mapRef.current;
    const maps = (window as any).google?.maps;
    if (!ready || !map || !maps || lastFrameKey.current === frameKey) return;
    lastFrameKey.current = frameKey;

    const wanted = new Set(frameIds);
    const targets = frameIds.length
      ? screens.filter((s) => wanted.has(s.screenId))
      : screens;
    if (targets.length === 0) return;

    if (isCoLocated(targets)) {
      map.setCenter({ lat: targets[0].latitude, lng: targets[0].longitude });
      map.setZoom(SINGLE_PIN_ZOOM);
      return;
    }
    const bounds = new maps.LatLngBounds();
    targets.forEach((s) => bounds.extend({ lat: s.latitude, lng: s.longitude }));
    map.fitBounds(bounds, 60);
  }, [ready, frameKey, frameIds, screens]);

  if (!isGoogleMapsConfigured || failed) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
        {failed
          ? "The map could not be loaded. Check your connection and refresh."
          : "The map is not available right now. Your screens are still listed alongside."}
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <div
        ref={containerRef}
        className="min-h-0 w-full flex-1 rounded-xl border border-gray-200"
        aria-label="Map of screens carrying your campaigns"
      />
      {ready && <MapLegend />}
    </div>
  );
};

export default CoverageMapCanvas;
